<template>
  <div class="mission-list-header" :class="{ 'is-fixed': isFixed }">
    <div class="mission-list-header__check-in" v-if="state.showCheckIn" :style="checkInStyle">
      <mission-check-in-section
        :checkInMissionsBeingHeldForThisSeason="checkInMissionsBeingHeldForThisSeason"
        :successfulCheckIn="successfulCheckIn"
        :isFixed="isFixed"
        @onClickCheckIn="handleCheckInClicked"
        @onClickClose="handleCloseCheckInClicked"
      />
    </div>
    <div class="mission-list-header__wrapper" ref="wrapper">
      <div class="mission-list-header__result">
        <div class="mission-list-header__container">
          <members-point-section
            class="mission-list-header__result-section"
            backLink="mission"
            :userData="userViewData"
            :membersRankData="membersRankData"
          />
        </div>
      </div>
      <div class="mission-list-header__tabs">
        <mission-list-tabs-parts />
      </div>
    </div>
    <div class="mission-list-header__year" ref="year">
      <div class="mission-list-header__container">
        <mission-year-selector-parts
          class="mission-list-header__year-selector"
          :selectedMissionYear="selectedMissionYear"
          :isFixed="isFixed"
          :showCheckIn="state.showCheckIn"
          @onClickCheckIn="handleShowCheckInClicked"
          @onChangeMissionYear="onChangeMissionYear"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, reactive, watch, ref } from '@vue/composition-api'
import MissionCheckInSection from '@/components/MissionPage/MissionListPane/MissionCheckInSection.vue'
import MissionYearSelectorParts from '@/components/MissionPage/MissionListPane/parts/MissionYearSelectorParts.vue'
import MembersPointSection from '@/components/MypageTopPage/MembershipCardPane/MembersPointSection.vue'
import MissionProgressDocument from '@/store/stores/collectionModule/documents/mission/MissionProgressDocument'
import CloudFrontUtil from '@/util/aws/CloudFrontUtil'
import Const from '@/util/Const'
import StoreUtil from '@/store/StoreUtil'
import useMembershipCard from '@/components/MypageTopPage/hook/useMembershipCard'
import MissionListTabsParts from '@/components/MissionPage/MissionListPane/parts/MissionListTabsParts.vue'
import { MembersRankDataType } from '@/components/MypageTopPage/MembershipCardPane.vue'
import { PointExpirationBackLinkType } from '@/views/MypagePage/MypagePointExpirationDatePage.vue'
import { UserPointWithGrantedSeasonYearType } from '@/store/hook/useUserPoint'

/**
 * ミッションページのヘッダー
 */
export default defineComponent({
  name: 'MissionListHeaderSection',
  components: {
    MissionListTabsParts,
    MembersPointSection,
    MissionYearSelectorParts,
    MissionCheckInSection,
  },
  props: {
    /**
     * ミッション一覧画面で表示する年度
     */
    selectedMissionYear: {
      type: Number,
      required: true,
    },
    /**
     * 今シーズン：開催中のラウンドチェックインまたはイベントチェックインミッション一覧
     */
    checkInMissionsBeingHeldForThisSeason: {
      type: Array as PropType<MissionProgressDocument[]>,
      required: true,
    },
    successfulCheckIn: {
      type: Boolean,
    },
    isFixed: {
      type: Boolean,
    },
    /**
     * 戻るボタンで戻る画面を指定
     */
    backLink: {
      type: String as PropType<PointExpirationBackLinkType>,
      default: 'mypage',
    },
  },
  setup(props, context) {
    const loginStore = StoreUtil.useStore('LoginStore')
    const mypagePageStore = StoreUtil.useStore('MypagePageStore')
    const contractInfoStore = StoreUtil.useStore('ContractInfoStore')
    const { getBadgeDisplayYearsInfo, getCurrentRank } = useMembershipCard()
    const { isFreePlan } = contractInfoStore
    const wrapper = ref<HTMLElement | null>(null)
    const year = ref<HTMLElement | null>(null)

    const state = reactive({
      showCheckIn: true,
    })

    const checkInStyle = computed(() => {
      if (!props.isFixed) {
        return {}
      }
      const wrapperHeight = wrapper.value?.getBoundingClientRect().height || 0
      const yearHeight = year.value?.getBoundingClientRect().height || 0
      const fixedHeaderHeight = wrapperHeight + yearHeight
      return {
        top: `${fixedHeaderHeight}px`,
      }
    })

    /**
     * バッジリストを取得する
     */
    const getBadgeList = () => {
      const results = []
      // sfgo-dev-supporterをロールに持つ場合、開発サポーターのバッジを付与する
      if (loginStore?.userRoles.includes('sfgo-dev-supporter')) {
        results.push({
          badgeType: 'supporter',
          label: '開発サポーター',
        })
      }
      // 登録日からの経過年数に応じたバッジ情報を取得する
      const elapsedYearResult = getBadgeDisplayYearsInfo(
        contractInfoStore?.ownOrganization.value?._createdDate,
      )
      if (elapsedYearResult) {
        results.push(elapsedYearResult)
      }
      return results
    }

    /**
     * ユーザーデータ
     */
    const userViewData = computed(() => ({
      name: mypagePageStore.user.value?.additionalData?.userDisplayName || '',
      image: CloudFrontUtil.getSignedUrl(mypagePageStore.user.value?.userPicture) || '',
      userRole: loginStore?.userRoles.find((r) =>
        Const.SFGO_ROLES.filter((v) => v !== 'sfgo-dev-supporter').includes(r),
      ),
      badgeList: getBadgeList(),
      isFreePlan,
    }))

    /**
     * 会員ランクデータ
     */
    const membersRankData = computed<MembersRankDataType>(() => {
      // Mapの先頭のデータを取得する
      const firstEntry: [string, UserPointWithGrantedSeasonYearType] =
        mypagePageStore.userPointsWithExpirationByExpirationDate.value.entries().next().value ||
        (['', { point: 0, grantedSeasonYear: 0 }] as [string, UserPointWithGrantedSeasonYearType])
      const [expirationDate, userPointsWithExpiration] = firstEntry
      return {
        point: mypagePageStore.currentOwnedPoints.value ?? 0,
        currentRank: getCurrentRank(mypagePageStore.currentOwnedPoints.value),
        expirationDate,
        expirationPoint: userPointsWithExpiration.point,
      }
    })

    const handleShowCheckInClicked = () => {
      state.showCheckIn = true
    }
    const handleCloseCheckInClicked = () => {
      state.showCheckIn = false
    }

    const handleCheckInClicked = () => {
      context.emit('onClickCheckIn')
    }

    /**
     * ミッション一覧画面の年度を変更
     */
    const onChangeMissionYear = (changedYear: number) => {
      context.emit('onChangeMissionYear', changedYear)
    }

    watch(
      () => props.isFixed,
      (val) => {
        state.showCheckIn = !val
      },
    )
    return {
      state,
      wrapper,
      year,
      checkInStyle,
      handleShowCheckInClicked,
      handleCloseCheckInClicked,
      handleCheckInClicked,
      userViewData,
      membersRankData,
      onChangeMissionYear,
    }
  },
})
</script>

<style scoped lang="scss">
.mission-list-header {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &.is-fixed {
    gap: 0;

    .mission-list-header__container {
      max-width: 600px;
      margin-right: auto;
      margin-left: auto;
    }

    .mission-list-header__check-in {
      position: absolute;
      top: 115px;
      z-index: 1;
      width: 100%;
    }

    .mission-list-header__wrapper {
      padding-bottom: 6px;
      background-color: $white10;
      box-shadow: 0 4px 14px 0 rgba(0, 0, 0, 0.25);
      backdrop-filter: blur(60px);
      border-bottom: 1px solid rgba(#fff, 0.2);

      .mission-list-header__result-section {
        background: none;
        border: none;
        border-radius: 0;
        backdrop-filter: none;
      }

      .mission-list-header__tabs {
        margin-top: 0;

        ::v-deep {
          .mission-list-tabs-parts__list {
            background-color: rgba(#000, 0.5);
          }
        }
      }
    }

    .mission-list-header__year {
      background-color: $white10;
      box-shadow: 0 4px 14px 0 rgba(0, 0, 0, 0.25);
      backdrop-filter: blur(60px);
      border-bottom: 1px solid rgba(#fff, 0.2);
    }
  }

  &__container {
    width: 100%;
  }

  &__result-section {
    border: 1px solid rgba(#fff, 0.2);
  }

  &__tabs {
    margin-top: 10px;
  }
}
</style>
